import React, {createContext, ReactNode, use, useOptimistic} from 'react';
import {useSelector} from 'react-redux';

import {selectBasket} from 'coreStore/data/basket';
import type {RootState} from 'coreStore/types';

type TMinicartCount = {
	/** Items in the basket (as `useBasket().itemsCount`), plus the ones of the adds still running. */
	count: number;
	/**
	 * Shows `quantity` more items until the action (or transition) calling it ends: call it inside one,
	 * before its first `await`. On error the count goes back to the basket's.
	 */
	addOptimisticItems: (quantity: number) => void;
};

const MinicartCountContext = createContext<TMinicartCount | null>(null);

/** `useBasket().itemsCount` (product quantities plus gift certificates), without the whole basket hook. */
const selectItemsCount = (state: RootState): number => {
	const basket = selectBasket(state);
	const productItemsCount = (basket?.productItems || []).reduce((sum, item) => sum + (item.quantity || 0), 0);

	return productItemsCount + (basket?.giftCertificateItems?.length || 0);
};

/**
 * Count of the minicart, updated optimistically by the add to cart actions (`useAddToCart`): the header
 * shows the new count as soon as the button is clicked, not when the basket API answers.
 */
export const MinicartCountProvider = ({children}: {children: ReactNode}) => {
	const itemsCount = useSelector(selectItemsCount);
	// The basket is updated in Redux before the action ends: the target count (not "+ quantity") keeps
	// the count right once the new basket arrives, instead of counting the added items twice.
	const [count, setOptimisticCount] = useOptimistic(itemsCount, (current: number, target: number) => Math.max(current, target));
	const addOptimisticItems = (quantity: number) => setOptimisticCount(count + quantity);

	return <MinicartCountContext value={{count, addOptimisticItems}}>{children}</MinicartCountContext>;
};

/**
 * Count of the minicart and its optimistic update (inside `MinicartCountProvider`, mounted by `AppRoot`).
 */
export const useMinicartCount = (): TMinicartCount => {
	const minicartCount = use(MinicartCountContext);

	if (!minicartCount) {
		throw new Error('useMinicartCount must be used inside MinicartCountProvider');
	}

	return minicartCount;
};
